import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play, Sparkles, ShieldAlert, ShieldCheck, Loader2,
} from "lucide-react";
import { Field } from "../shared.jsx";

export default function VoiceLogsModerator() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    // Which log ids are currently being AI-analysed. Keyed by id → bool.
    const [analysing, setAnalysing] = useState({});

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/admin/voice_logs", { params: filter ? { status: filter } : {} });
            setItems(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [filter]);

    async function setStatus(id, status) {
        try {
            await api.put(`/admin/voice_logs/${id}/status`, { status });
            toast.success(`Marked ${status}`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    }
    async function remove(id) {
        if (!window.confirm("Delete this voice log permanently?")) return;
        try {
            await api.delete(`/admin/voice_logs/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    }

    async function analyse(id) {
        setAnalysing((m) => ({ ...m, [id]: true }));
        try {
            const r = await api.post(`/admin/voice_logs/${id}/analyze`);
            // Merge the fresh analysis into the local list so we don't refetch.
            setItems((list) => list.map((v) => v.id === id ? { ...v, ai_analysis: r.data.analysis } : v));
            const verdict = r.data.analysis?.moderation;
            if (verdict?.safe) {
                toast.success("Analysed — content looks safe.");
            } else {
                toast.warning(`Analysed — flagged (${verdict?.categories?.join(", ") || "review needed"}).`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAnalysing((m) => { const { [id]: _, ...rest } = m; return rest; });
        }
    }

    // Build a token-authenticated audio URL so admin can preview pending audio
    // (the public /audio endpoint blocks non-approved logs by design).
    const authAudioUrl = (id) => {
        const token = localStorage.getItem("token") || "";
        // We stream through the admin endpoint using fetch → object URL, but for
        // simplicity here we rely on Authorization headers via a Blob loader
        // when the user clicks Play. See <AuthAudio /> below.
        return { id, token };
    };

    return (
        <section data-testid="voicelogs-panel" className="space-y-6">
            <header className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-serif text-2xl tracking-tight">Voice Logs</h2>
                    <p className="text-sm text-[var(--sb-text-dim)] mt-1">Approve visitor-submitted voice notes before they go live on the map.</p>
                </div>
                <div className="flex gap-2">
                    {[
                        { id: "pending", label: "Pending" },
                        { id: "approved", label: "Approved" },
                        { id: "rejected", label: "Rejected" },
                        { id: "", label: "All" },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`px-4 py-1.5 text-xs uppercase tracking-[0.22em] border transition-colors ${
                                filter === f.id
                                    ? "bg-[var(--sb-brand)] text-[var(--sb-bg)] border-[var(--sb-brand)]"
                                    : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:border-[var(--sb-brand)]"
                            }`}
                            data-testid={`voicelogs-filter-${f.id || "all"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </header>

            {loading ? (
                <div className="text-[var(--sb-text-dim)]">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card p-10 text-center text-[var(--sb-text-dim)]">No voice logs in this state.</div>
            ) : (
                <div className="space-y-4">
                    {items.map((v) => (
                        <div key={v.id} className="border border-[var(--sb-border)] p-5 space-y-3" data-testid={`voicelogs-item-${v.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <h3 className="font-serif text-lg tracking-tight">
                                        {v.name} · <span className="italic text-[var(--sb-brand)]">{v.location}</span>
                                    </h3>
                                    <p className="text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mt-1">
                                        {v.region || "no region"} · {Math.round(v.duration_seconds || 0)}s · {Math.round((v.audio_bytes || 0) / 1024)} KB · {v.status}
                                    </p>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={() => analyse(v.id)}
                                        disabled={!!analysing[v.id]}
                                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                                        style={{ padding: "0.4rem 0.8rem" }}
                                        data-testid={`voicelogs-analyse-${v.id}`}
                                        title="Transcribe with Whisper + moderate with Claude Sonnet 4.5"
                                    >
                                        {analysing[v.id]
                                            ? <><Loader2 size={14} className="animate-spin" /> Analysing…</>
                                            : <><Sparkles size={14} /> {v.ai_analysis ? "Re-analyse" : "Analyse"}</>
                                        }
                                    </button>
                                    {v.status !== "approved" && (
                                        <button onClick={() => setStatus(v.id, "approved")} className="btn-primary text-xs" style={{ padding: "0.4rem 0.8rem" }} data-testid={`voicelogs-approve-${v.id}`}>
                                            <Check size={14} /> Approve
                                        </button>
                                    )}
                                    {v.status !== "rejected" && (
                                        <button onClick={() => setStatus(v.id, "rejected")} className="btn-outline text-xs" style={{ padding: "0.4rem 0.8rem" }} data-testid={`voicelogs-reject-${v.id}`}>
                                            <Ban size={14} /> Reject
                                        </button>
                                    )}
                                    <button onClick={() => remove(v.id)} className="btn-outline text-xs" style={{ padding: "0.4rem 0.8rem" }} data-testid={`voicelogs-delete-${v.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            {v.message && (
                                <p className="text-sm text-[var(--sb-text-dim)] italic">&ldquo;{v.message}&rdquo;</p>
                            )}
                            {v.ai_analysis && <AIVerdict analysis={v.ai_analysis} />}
                            <AuthAudio id={v.id} />
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/**
 * AuthAudio — plays admin-only audio by fetching it with the Bearer token then
 * turning the blob into a local object URL so the <audio> element can consume
 * it without needing the token in the querystring.
 */
function AuthAudio({ id }) {
    const [url, setUrl] = useState("");
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const res = await api.get(`/admin/voice_logs/${id}/audio`, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(res.data);
            setUrl(blobUrl);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    if (!url) {
        return (
            <button
                type="button"
                onClick={load}
                className="btn-outline text-xs inline-flex items-center gap-2"
                style={{ padding: "0.4rem 0.8rem" }}
                data-testid={`voicelogs-load-${id}`}
                disabled={loading}
            >
                <Play size={14} /> {loading ? "Loading…" : "Load audio"}
            </button>
        );
    }
    return <audio src={url} controls preload="metadata" className="w-full" data-testid={`voicelogs-audio-${id}`} />;
}

/**
 * AIVerdict — pretty presentation of the Whisper transcript and the
 * Claude Sonnet 4.5 moderation verdict. Colour coded by severity + safe flag.
 */
function AIVerdict({ analysis }) {
    if (!analysis) return null;
    const { transcript, moderation, transcriber, moderator, analysed_at } = analysis;
    const safe = !!moderation?.safe;
    const severity = moderation?.severity || "medium";
    const categories = moderation?.categories || [];
    const reason = moderation?.reason || "";

    const tint = safe
        ? "border-emerald-500/30 bg-emerald-500/[0.04]"
        : severity === "high"
            ? "border-rose-500/40 bg-rose-500/[0.06]"
            : "border-amber-500/40 bg-amber-500/[0.05]";
    const Icon = safe ? ShieldCheck : ShieldAlert;
    const iconColor = safe ? "text-emerald-400" : severity === "high" ? "text-rose-400" : "text-amber-400";

    return (
        <div className={`border ${tint} px-4 py-3 space-y-3`} data-testid="voicelogs-ai-verdict">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Icon size={16} className={iconColor} />
                    <span className="text-xs uppercase tracking-[0.28em] text-[var(--sb-text-dim)]">
                        {safe ? "Safe to publish" : "Flagged for review"}
                    </span>
                    {!safe && (
                        <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border ${severity === "high" ? "border-rose-500/40 text-rose-300" : "border-amber-500/40 text-amber-300"}`}>
                            {severity}
                        </span>
                    )}
                </div>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sb-text-muted)]">
                    {transcriber} + {moderator?.replace("claude-", "claude ")}
                </span>
            </div>
            {reason && (
                <p className="text-xs text-[var(--sb-text-dim)]">
                    <span className="uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mr-2">Reason</span>
                    {reason}
                </p>
            )}
            {categories.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="uppercase text-[10px] tracking-[0.22em] text-[var(--sb-text-muted)]">Categories</span>
                    {categories.map((c) => (
                        <span key={c} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border border-[var(--sb-border)] text-[var(--sb-text-dim)]">{c}</span>
                    ))}
                </div>
            )}
            {transcript && (
                <div>
                    <div className="uppercase text-[10px] tracking-[0.22em] text-[var(--sb-text-muted)] mb-1">Transcript</div>
                    <p className="text-sm text-[var(--sb-text)] italic leading-relaxed" data-testid="voicelogs-transcript">
                        &ldquo;{transcript}&rdquo;
                    </p>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
